// A suspense-friendly version of the <GoogleMap/> component.
//
// Ambassador: Yatharth
//
import { Atom, atom, useAtomValue } from 'jotai';

import { QhpWithLocationInfo } from '../../../fetching/api/liveLocations';
import { ReturnComponent } from '../../../utils/reactTypes';
import { GoogleMap, GoogleMapOtherProps, GoogleMapStopsProp } from './GoogleMap';

/*

Q: Why does this exist?

A: With the normal <GoogleMap/> component, it was difficult for allow child components
 to update the <GoogleMap/> component state. It would cause subtle, tricky
 infinite re-render loops.

For example, what if a page wanted to put most of its queries into a child
 wrapped with suspense, so that the page itself loads quickly, and the child
 slowly loads its data? We could try:

    <AxlePage map={<GoogleMap visits={myVisits} />>
      ...
      <AxleSuspense>
         <MyChildComponent/>
      </AxleSuspense>
    </AxlePage>

But how can the parent pass in `myVisits` if it doesn't have access to the
 query data? The parent could manage some state and pass a callback to its
 child. For example:

    const [myVisits, setMyVisits] = useState([])

    return (
      <AxlePage map={<GoogleMap visits={myVisits} />>
        ...
        <AxleSuspense>
          <MyChildComponent setMyVisits={setMyVisits} />
        </AxleSuspense>
      </AxlePage>
    );

But this is awkward. Not only that, it would cause infinite re-render loops
 if the child wasn't careful. The child might update the maps state, then
 the parent would re-render, which might cause the child to rerender, etc.

The fundamental issue is that a rerender of the <GoogleMaps/> component should not
 necessitate a rerender of the parent.

To accomplish this, we have this <GoogleMapWithGlobalState/> component
 that does not take any changing props. Instead, it uses a Jotai atom to manage
 its state. This way, the child can update the state of the map without triggering
 a rerender of the parent that has the <AxlePage map={...}/> component.

*/

const emptyQhpWithLocationInfoAtom = atom<QhpWithLocationInfo[]>([]);

export const GoogleMapWithAtom = ({
    stopsAtom,
    qhpsWithLocationInfoAtom = emptyQhpWithLocationInfoAtom,
    ...otherProps
}: {
    stopsAtom: Atom<GoogleMapStopsProp>;
    qhpsWithLocationInfoAtom?: Atom<QhpWithLocationInfo[]>;
} & GoogleMapOtherProps): ReturnComponent => {
    const stops = useAtomValue(stopsAtom);
    const qhpsWithLocationInfo = useAtomValue(qhpsWithLocationInfoAtom);
    return <GoogleMap stops={stops} qhpsWithLocationInfo={qhpsWithLocationInfo} {...otherProps} />;
};
